"use client";

import { Children, useCallback, useEffect, useRef, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react/dist/ssr";

import { cn } from "@/src/lib/utils";

import type { CarouselProps } from "./Carousel.types";

import "./carousel.css";

// Center-mode, peek-style carousel: slides are wider than one-per-view so
// neighbors show at the edges, snapping to center on scroll/swipe/arrow/dot.
// Built on native CSS scroll-snap rather than a JS-driven track transform —
// gets touch/trackpad momentum, keyboard scrolling, and correct RTL behavior
// for free, and never needs a resize observer to recompute slide widths.
export function Carousel({
  children,
  className,
  slideClassName,
  ariaLabel,
  showDots = true,
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const slides = Children.toArray(children);

  const scrollToIndex = useCallback((index: number) => {
    slideRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let raf = 0;

    // Edge detection uses real scroll position, not activeIndex: with peeking
    // slides the first/last slide can never reach the track center, so the
    // active index alone can't tell when there's nothing left to scroll.
    const updateEdges = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      setCanScrollPrev(track.scrollLeft > 1);
      setCanScrollNext(track.scrollLeft < maxScroll - 1);
    };

    const handleScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const trackCenter = track.scrollLeft + track.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        slideRefs.current.forEach((slide, index) => {
          if (!slide) return;
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - trackCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        setActiveIndex(closestIndex);
        updateEdges();
      });
    };

    updateEdges();
    const resizeObserver = new ResizeObserver(updateEdges);
    resizeObserver.observe(track);

    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="relative">
      <div
        ref={trackRef}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        className={cn(
          "carousel-track flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth sm:gap-6",
          className,
        )}
      >
        {slides.map((child, index) => (
          <div
            key={index}
            ref={(el) => {
              slideRefs.current[index] = el;
            }}
            className={cn("shrink-0 snap-center", slideClassName)}
          >
            {child}
          </div>
        ))}
      </div>

      <button
        type="button"
        aria-label="Previous slide"
        onClick={() => scrollToIndex(Math.max(activeIndex - 1, 0))}
        disabled={!canScrollPrev}
        className="absolute top-1/2 left-2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-neutral-700 text-neutral-50 shadow-lg transition-opacity disabled:pointer-events-none disabled:opacity-0"
      >
        <CaretLeft size={18} weight="bold" />
      </button>

      <button
        type="button"
        aria-label="Next slide"
        onClick={() => scrollToIndex(Math.min(activeIndex + 1, slides.length - 1))}
        disabled={!canScrollNext}
        className="absolute top-1/2 right-2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-neutral-700 text-neutral-50 shadow-lg transition-opacity disabled:pointer-events-none disabled:opacity-0"
      >
        <CaretRight size={18} weight="bold" />
      </button>

      {showDots && slides.length > 1 && (
        <div className="mt-5 flex justify-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === activeIndex}
              onClick={() => scrollToIndex(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                index === activeIndex ? "w-6 bg-neutral-700" : "w-2 bg-neutral-300",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
